import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, Send, Loader2, AlertTriangle, CalendarClock, Lock, Sparkles, PartyPopper, ClipboardCheck,
} from 'lucide-react';
import { API, fmtDate } from '../../lib/planConstants';

const QUICK = [
  "What's my next best move?",
  'Break my next task into small steps',
  "I'm behind, help me reset my dates",
  'Does this pathway still fit me?',
];

export default function CoachPanel({ signals, progress, checkinDue, authHeader, onOpenTask, onRefresh, focusTask, clearFocus }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [checkin, setCheckin] = useState({ open: false, blockers: '', mood: '', fits: true, done: new Set() });
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  useEffect(() => {
    if (focusTask) {
      send(`Help me with this task: "${focusTask.title}". ${focusTask.description || ''}`);
      clearFocus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTask]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: 'student', content: msg }]);
    setInput('');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/coach/chat`,
        { message: msg, session_id: sessionId }, authHeader);
      setSessionId(r.data.session_id);
      setMessages((m) => [...m, { role: 'coach', content: r.data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Your coach is unavailable right now.');
    } finally {
      setBusy(false);
    }
  };

  const submitCheckin = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/futurepath-plan/coach/checkin`, {
        completed_task_ids: [...checkin.done],
        blockers: checkin.blockers, mood: checkin.mood, pathway_still_fits: checkin.fits,
      }, authHeader);
      setMessages((m) => [...m, { role: 'coach', content: r.data.checkin.summary }]);
      setCheckin({ open: false, blockers: '', mood: '', fits: true, done: new Set() });
      toast.success('Check-in saved');
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save your check-in.');
    } finally {
      setBusy(false);
    }
  };

  const s = signals || {};
  const toggleDone = (id) => {
    const next = new Set(checkin.done);
    next.has(id) ? next.delete(id) : next.add(id);
    setCheckin({ ...checkin, done: next });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="coach-panel">
      <div className="lg:col-span-2 space-y-4">
        {s.next_best_action && (
          <div className="glass-card p-5" data-testid="next-best-action">
            <p className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Next best action
            </p>
            <button onClick={() => onOpenTask(s.next_best_action)} className="text-left mt-2 w-full">
              <p className="text-base font-semibold text-white hover:text-cyan-200 transition-colors duration-200">{s.next_best_action.title}</p>
              <p className="text-xs text-slate-300 mt-1">Due {fmtDate(s.next_best_action.due_date)} · {s.next_best_action.estimated_effort || 'quick win'}</p>
            </button>
            {s.next_best_action.why && <p className="text-sm text-slate-300 mt-2">{s.next_best_action.why}</p>}
          </div>
        )}

        {(checkinDue || checkin.open) && (
          <div className="glass-card p-5 border-cyan-500/30" data-testid="checkin-card">
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <ClipboardCheck className="w-4 h-4 text-cyan-300" /> Weekly check-in
            </p>
            {!checkin.open ? (
              <>
                <p className="text-sm text-slate-300 mt-1">Two minutes: tell your coach what got done and what's in the way.</p>
                <button onClick={() => setCheckin({ ...checkin, open: true })}
                  className="btn-primary mt-3 px-4 py-2 rounded-full text-xs font-semibold"
                  data-testid="start-checkin-btn">Start check-in</button>
              </>
            ) : (
              <div className="mt-3 space-y-3">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-1.5">What did you finish?</p>
                  <div className="space-y-1.5 max-h-40 overflow-y-auto">
                    {[...(s.due_soon || []), ...(s.overdue || [])].slice(0, 8).map((t) => (
                      <label key={t.id} className="flex items-center gap-2 text-sm text-slate-300">
                        <input type="checkbox" checked={checkin.done.has(t.id)} onChange={() => toggleDone(t.id)}
                          data-testid={`checkin-task-${t.id}`} />
                        {t.title}
                      </label>
                    ))}
                    {![...(s.due_soon || []), ...(s.overdue || [])].length && (
                      <p className="text-xs text-slate-300">Nothing due right now.</p>
                    )}
                  </div>
                </div>
                <input value={checkin.blockers} onChange={(e) => setCheckin({ ...checkin, blockers: e.target.value })}
                  placeholder="What's blocking you?" data-testid="checkin-blockers"
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white" />
                <label className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={checkin.fits} onChange={(e) => setCheckin({ ...checkin, fits: e.target.checked })}
                    data-testid="checkin-pathway-fits" />
                  This pathway still feels right for me
                </label>
                <button onClick={submitCheckin} disabled={busy}
                  className="btn-primary px-4 py-2 rounded-full text-xs font-semibold" data-testid="submit-checkin-btn">
                  {busy ? 'Saving…' : 'Submit check-in'}
                </button>
              </div>
            )}
          </div>
        )}

        {(s.overdue || []).length > 0 && (
          <div className="glass-card p-5" data-testid="overdue-list">
            <p className="text-sm font-semibold text-rose-300 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Overdue ({s.overdue.length})
            </p>
            <ul className="mt-2 space-y-1.5">
              {s.overdue.map((t) => (
                <li key={t.id}>
                  <button onClick={() => onOpenTask(t)} className="text-sm text-slate-300 hover:text-white text-left">
                    {t.title} <span className="text-xs text-rose-300/80">· was due {fmtDate(t.due_date)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(s.due_soon || []).length > 0 && (
          <div className="glass-card p-5" data-testid="due-soon-list">
            <p className="text-sm font-semibold text-white flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-cyan-300" /> Next 14 days
            </p>
            <ul className="mt-2 space-y-1.5">
              {s.due_soon.map((t) => (
                <li key={t.id}>
                  <button onClick={() => onOpenTask(t)} className="text-sm text-slate-300 hover:text-white text-left">
                    {t.title} <span className="text-xs text-slate-300">· {fmtDate(t.due_date)}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {(s.blocked || []).length > 0 && (
          <div className="glass-card p-5" data-testid="blocked-list">
            <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
              <Lock className="w-4 h-4" /> Waiting on something else
            </p>
            <ul className="mt-2 space-y-1.5">
              {s.blocked.map((t) => (
                <li key={t.id} className="text-sm text-slate-300">{t.title}</li>
              ))}
            </ul>
          </div>
        )}

        {(s.milestones_completed || []).length > 0 && (
          <div className="glass-card p-5" data-testid="celebrate-list">
            <p className="text-sm font-semibold text-amber-300 flex items-center gap-2">
              <PartyPopper className="w-4 h-4" /> Milestones you've cleared
            </p>
            <ul className="mt-2 space-y-1">
              {s.milestones_completed.map((t, i) => <li key={i} className="text-sm text-slate-300">{t}</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="lg:col-span-3 glass-card p-5 flex flex-col" data-testid="coach-chat">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-800">
          <Bot className="w-4 h-4 text-cyan-300" />
          <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-cyan-200">Agentic FuturePath AI Coach</h3>
          {progress && <span className="ml-auto text-xs text-slate-300">{progress.percent}% of plan complete</span>}
        </div>

        <div className="flex-1 min-h-[280px] max-h-[460px] overflow-y-auto py-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-sm text-slate-300">
              <p>I can see your whole plan, deadlines, blockers, milestones.</p>
              <p className="mt-1">Ask me anything, or pick a starter below.</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`max-w-[92%] rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${
              m.role === 'student' ? 'ml-auto bg-cyan-500/15 text-cyan-50' : 'bg-slate-900/70 text-slate-200'}`}
              data-testid={`coach-msg-${m.role}`}>
              {m.content}
            </div>
          ))}
          {busy && <div className="text-xs text-slate-300 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Coach is thinking…</div>}
          <div ref={endRef} />
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          {QUICK.map((q) => (
            <button key={q} onClick={() => send(q)} disabled={busy}
              className="text-[11px] px-2.5 py-1.5 rounded-full border border-slate-700 text-slate-300 hover:border-cyan-500/50 hover:text-cyan-200 transition-colors duration-200"
              data-testid={`coach-quick-${q.slice(0, 10)}`}>{q}</button>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-3">
          <input value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send()}
            placeholder="Ask your coach…" data-testid="coach-input"
            className="flex-1 bg-slate-900 border border-slate-700 rounded-full px-4 py-2.5 text-sm text-white" />
          <button onClick={() => send()} disabled={busy} className="btn-primary p-2.5 rounded-full" data-testid="coach-send-btn">
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
